import React, { useState } from 'react';
import { ClockIcon, HeadphonesIcon, HeartPulseIcon, MessageSquareIcon, PhoneIcon, ShieldAlertIcon, SirenIcon, UserCheckIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { Modal } from '../../components/ui/Modal';
import { Field, Input, Select, Textarea } from '../../components/ui/Field';
import { ChoiceGroup, Toggle } from '../../components/ui/ChoiceGroup';
import { HelpCentre } from '../../components/support/HelpCentre';
import { alerts, counsellingSlots, emergencyContacts, feedbackThreads, openSurveys, trustedContacts, wellbeingResources } from '../../data/support';
import { useAccount } from '../../contexts/AccountContext';
import { useSafety } from '../../contexts/SafetyContext';
import { responderForCampus } from '../../data/safety';
import { AlertTracker } from '../../components/safety/AlertTracker';
import { EmergencyDialog } from '../../components/safety/EmergencyDialog';
const TABS = ['Help & tickets', 'Safety', 'Feedback', 'Wellbeing'] as const;
const CATEGORIES = ['Theft', 'Harassment', 'Medical', 'Unsafe environment', 'Other'] as const;
const FEEDBACK_VISIBILITY = ['Public', 'Private', 'Confidential'] as const;
export function Support() {
  const {
    account
  } = useAccount();
  const {
    alerts,
    incidents,
    fileIncident
  } = useSafety();
  const responder = responderForCampus(account.campusId);
  const [emergencyOpen, setEmergencyOpen] = useState(false);
  const myAlerts = alerts.filter(alert => alert.raisedBy.handle === account.username);
  const [tab, setTab] = useState<(typeof TABS)[number]>('Help & tickets');
  const [incidentOpen, setIncidentOpen] = useState(false);
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>('Theft');
  const [anonymous, setAnonymous] = useState(false);
  const [incidentPlace, setIncidentPlace] = useState('');
  const [incidentDetail, setIncidentDetail] = useState('');
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [visibility, setVisibility] = useState<(typeof FEEDBACK_VISIBILITY)[number]>('Private');
  return <div className="space-y-6" data-id="element-11725" data-mp="edf6euz65mqk">
      <PageHeader eyebrow="Support" title="Help and support" description="Get an answer, raise a ticket, report what is happening around campus, tell your institution what needs fixing, and find wellbeing support." data-id="element-11726" data-mp="ecm7gg6gc1iz" />

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Support sections" data-id="element-11727" data-mp="3mldu0t68irgi" />

      {tab === 'Help & tickets' && <HelpCentre data-id="element-11728" data-mp="3rk79us8zuuju" />}

      {tab === 'Safety' && <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-11729" data-mp="3rkpcq7q5w70q">
          <div className="space-y-6" data-id="element-11730" data-mp="1kv2dfaaan2k4">
            <div className="flex flex-col gap-4 rounded-card border border-danger-100 bg-danger-50 px-5 py-4 sm:flex-row sm:items-center" data-id="element-11731" data-mp="1krwkfjyjngj4">
              <SirenIcon className="h-6 w-6 shrink-0 text-danger-700" data-id="element-11732" data-mp="3aalfj5orh0o4" />
              <div className="min-w-0 flex-1" data-id="element-11733" data-mp="35c1rmzqlxet4">
                <p className="text-body font-semibold text-ink" data-id="element-11734" data-mp="35d36zgenongn">Need help right now?</p>
                <p className="mt-0.5 max-w-[64ch] text-body-sm text-ink" data-id="element-11735" data-mp="3k17rmpx9r922">
                  Call{' '}
                  <span className="cz-num font-semibold" data-id="element-11736" data-mp="3f96fr540jkck">
                    {responder?.phone ?? '112'}
                  </span>{' '}
                  first — a phone call reaches a person faster than any app.{' '}
                  {responder ? responder.channel === 'console' ? `${responder.unit} also works the Campuzon safety desk, so an alert here reaches them directly.` : responder.channel === 'relay' ? `${responder.unit} does not work the Campuzon desk; an alert here is relayed to their control room by phone.` : `Campuzon has no response agreement with ${responder.unit} yet, so an alert here would be recorded but not delivered.` : 'No campus response unit is configured for your campus.'}
                </p>
              </div>
              <Button variant="primary" className="shrink-0 border-danger-700 bg-danger-700 hover:border-[#991b1b] hover:bg-[#991b1b]" onClick={() => setEmergencyOpen(true)} data-id="element-11737" data-mp="2f2auzhg1fcq3">
                Raise an emergency alert
              </Button>
            </div>

            {myAlerts.length > 0 && <div className="space-y-5" data-id="element-11738" data-mp="1rcqb8wc28bfr">
                {myAlerts.map(alert => <AlertTracker key={alert.id} alert={alert} data-id="element-11739" data-mp="1w9yoqjm9jrao" />)}
              </div>}

            <Panel title="Campus alerts" description="Issued by campus security for your campus." bodyClassName="p-0" data-id="element-11740" data-mp="214cqd3riquns">
              <ul className="divide-y divide-line" data-id="element-11741" data-mp="18knwjqizb585">
                {alerts.map(alert => <li key={alert.id} className="flex gap-3 px-5 py-4" data-id="element-11742" data-mp="3luxk5b1dwaei">
                    <ShieldAlertIcon className={`mt-0.5 h-4 w-4 shrink-0 ${alert.severity === 'critical' ? 'text-danger-700' : alert.severity === 'warning' ? 'text-warn-700' : 'text-ink-subtle'}`} data-id="element-11743" data-mp="3hu3r639345er" />
                    <div className="min-w-0" data-id="element-11744" data-mp="2y64hkvq9b4ax">
                      <p className="text-[13px] font-semibold text-ink" data-id="element-11745" data-mp="2y5cyulhrd122">{alert.title}</p>
                      <p className="mt-0.5 text-[13px] leading-5 text-ink-muted" data-id="element-11746" data-mp="2u5kjnpxuzwnq">{alert.detail}</p>
                      <p className="mt-1 text-[12px] text-ink-subtle" data-id="element-11747" data-mp="2p7j59ibdolil">
                        {alert.area} · {alert.issued}
                      </p>
                    </div>
                  </li>)}
              </ul>
            </Panel>

            <Panel title="Your reports" description="Reports you have filed and where they have got to." action={<Button size="sm" variant="secondary" onClick={() => setIncidentOpen(true)} data-id="element-11749" data-mp="2ah6aocw19dvd">
                  Report an incident
                </Button>} bodyClassName="p-0" data-id="element-11748" data-mp="25jmxlgb2n750">
              <ul className="divide-y divide-line" data-id="element-11750" data-mp="20mwfknfpi1gy">
                {incidents.map(incident => <li key={incident.id} className="px-5 py-4" data-id="element-11751" data-mp="2t9r0flgfr2uy">
                    <div className="flex flex-wrap items-start gap-3" data-id="element-11752" data-mp="2t98xy736jsel">
                      <div className="min-w-0 flex-1" data-id="element-11753" data-mp="2fj5c3h87q0po">
                        <p className="cz-num text-body-sm font-semibold text-ink" data-id="element-11754" data-mp="2fgj6hn4vtva3">
                          {incident.reference} · {incident.category}
                        </p>
                        <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-11755" data-mp="3788kn974tx84">{incident.location}</p>
                        <p className="mt-0.5 text-caption text-ink-subtle" data-id="element-11756" data-mp="3c1jjoo5pd9ph">
                          Reported {incident.reported}
                          {incident.anonymous ? ' · filed anonymously' : ''}
                        </p>
                      </div>
                      <Badge tone={incident.status === 'Resolved' ? 'ok' : incident.status === 'Received' ? 'warn' : 'brand'} data-id="element-11757" data-mp="1abcdtua69n81">
                        {incident.status}
                      </Badge>
                    </div>
                    <p className="mt-2 max-w-[70ch] rounded-md border border-line bg-canvas px-3 py-2 text-body-sm text-ink" data-id="element-11758" data-mp="rlw5wlw5qshl">
                      {incident.responderNote ?? 'No response recorded yet. When security changes the status they must say why, and it appears here.'}
                    </p>
                  </li>)}
              </ul>
            </Panel>
          </div>

          <div className="space-y-6" data-id="element-11759" data-mp="3rhjfixao0inl">
            <Panel title="Emergency contacts" bodyClassName="p-0" data-id="element-11760" data-mp="cb4l6v5lgb4">
              <ul className="divide-y divide-line" data-id="element-11761" data-mp="5c5zrmlyos1z">
                {emergencyContacts.map(contact => <li key={contact.label} className="px-5 py-3" data-id="element-11762" data-mp="a2meda1iqjpx">
                    <p className="text-[13px] font-medium text-ink" data-id="element-11763" data-mp="a4z4wblce2el">{contact.label}</p>
                    <p className="cz-num mt-0.5 flex items-center gap-1.5 text-[13px] text-brand-700" data-id="element-11764" data-mp="f1pj0tdl6nxd">
                      <PhoneIcon className="h-3 w-3" data-id="element-11765" data-mp="j1rc4e59a911" />
                      {contact.value}
                    </p>
                    <p className="text-[12px] text-ink-subtle" data-id="element-11766" data-mp="o0uhjzp5eh9j">{contact.note}</p>
                  </li>)}
              </ul>
            </Panel>
            <Panel title="Trusted contacts" bodyClassName="p-0" data-id="element-11767" data-mp="2pvbu5w3si310">
              <ul className="divide-y divide-line" data-id="element-11768" data-mp="2uslgv3zso0l5">
                {trustedContacts.map(contact => <li key={contact.name} className="flex items-start gap-2.5 px-5 py-3" data-id="element-11769" data-mp="2na40qexexnj7">
                    <UserCheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-subtle" data-id="element-11770" data-mp="2ij5i38oycdcs" />
                    <div data-id="element-11771" data-mp="2djj7z84f7d87">
                      <p className="text-[13px] font-medium text-ink" data-id="element-11772" data-mp="2dkubd6tyeiys">{contact.name}</p>
                      <p className="text-[12px] text-ink-muted" data-id="element-11773" data-mp="28lhdlr1jnrhf">
                        {contact.relationship} · {contact.phone}
                      </p>
                    </div>
                  </li>)}
              </ul>
              <div className="border-t border-line px-5 py-3" data-id="element-11774" data-mp="31a54c252b92d">
                <p className="text-[12px] leading-5 text-ink-muted" data-id="element-11775" data-mp="2w9he3ionqw97">
                  Trusted contacts are only notified when you raise an emergency alert.
                </p>
              </div>
            </Panel>
          </div>
        </div>}

      {tab === 'Feedback' && <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-11776" data-mp="1fvivygpoiwms">
          <Panel title="Your feedback" description="Public feedback is visible to other students. Private and confidential feedback goes only to the responsible office." action={<Button size="sm" variant="secondary" onClick={() => setFeedbackOpen(true)} data-id="element-11778" data-mp="2jll02d9g3y4p">
                New feedback
              </Button>} bodyClassName="p-0" data-id="element-11777" data-mp="1fydeu3t1o9e3">
            <ul className="divide-y divide-line" data-id="element-11779" data-mp="2sm9db5r0lugj">
              {feedbackThreads.map(thread => <li key={thread.id} className="px-5 py-4" data-id="element-11780" data-mp="2ol7m9c794opn">
                  <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-11781" data-mp="2oko7g4cwegfu">
                    <div className="min-w-0" data-id="element-11782" data-mp="32k72bvgp7va7">
                      <p className="text-[13px] font-semibold text-ink" data-id="element-11783" data-mp="2xlvrpiikxgqx">{thread.subject}</p>
                      <p className="mt-0.5 text-[13px] text-ink-muted" data-id="element-11784" data-mp="186o9gyxm50v8">{thread.target}</p>
                      <p className="mt-0.5 text-[12px] text-ink-subtle" data-id="element-11785" data-mp="188isg7pbqiwo">
                        {thread.kind} · submitted {thread.submitted}
                        {thread.responseDays ? ` · responded in ${thread.responseDays} days` : ''}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2" data-id="element-11786" data-mp="3jw48wkk3jzyi">
                      <Badge data-id="element-11787" data-mp="354ltanv5g7fo">{thread.visibility}</Badge>
                      <Badge tone={thread.status === 'Resolved' ? 'ok' : thread.status === 'Acknowledged' ? 'brand' : 'warn'} data-id="element-11788" data-mp="3a3yd0zqq4ye1">
                        {thread.status}
                      </Badge>
                    </div>
                  </div>
                </li>)}
            </ul>
          </Panel>

          <Panel title="Open surveys" bodyClassName="p-0" data-id="element-11789" data-mp="kg7jsf3ji1g7">
            <ul className="divide-y divide-line" data-id="element-11790" data-mp="pdqx9cseya73">
              {openSurveys.map(survey => <li key={survey.id} className="px-5 py-4" data-id="element-11791" data-mp="nwk5i37nfdug">
                  <p className="text-[13px] font-semibold text-ink" data-id="element-11792" data-mp="ny50tv1sf0td">{survey.title}</p>
                  <p className="mt-0.5 text-[12px] text-ink-muted" data-id="element-11793" data-mp="iwz31it9sncz">{survey.owner}</p>
                  <p className="cz-num mt-1 flex items-center gap-1.5 text-[12px] text-ink-subtle" data-id="element-11794" data-mp="dzp6lj7grbfn">
                    <ClockIcon className="h-3 w-3" data-id="element-11795" data-mp="9y3z6ddp7pcu" />
                    About {survey.minutes} minutes · {survey.closes}
                  </p>
                  {survey.anonymous && <Badge className="mt-2" data-id="element-11796" data-mp="11nynq7tqfb0g">Responses are anonymous</Badge>}
                  <Button size="sm" variant="secondary" className="mt-2.5" data-id="element-11797" data-mp="wx09rew7up0d">
                    Start survey
                  </Button>
                </li>)}
            </ul>
          </Panel>
        </div>}

      {tab === 'Wellbeing' && <div className="space-y-6" data-id="element-11798" data-mp="1pfxb3q6ggwty">
          <div className="rounded-card border border-line bg-surface px-5 py-4" data-id="element-11799" data-mp="1kmkqdr4wda6h">
            <p className="flex items-start gap-2 text-[13px] leading-6 text-ink-muted" data-id="element-11800" data-mp="33a440olpb2bd">
              <HeartPulseIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand-700" data-id="element-11801" data-mp="388xj2lgmz1dn" />
              Campuzon is not a replacement for medical or psychological care. If you are in danger or
              in crisis, call the campus health centre on{' '}
              <span className="cz-num font-medium text-ink" data-id="element-11802" data-mp="3haew1uyqymy4">+234 803 000 2222</span> or the national
              emergency line on <span className="cz-num font-medium text-ink" data-id="element-11803" data-mp="3m86wjddgjhdx">112</span>.
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-11804" data-mp="3r5yqse1krfla">
            <Panel title="Self-help and education" bodyClassName="p-0" data-id="element-11805" data-mp="3vzkw1l5esh72">
              <ul className="divide-y divide-line" data-id="element-11806" data-mp="3v8u8zt70cgl">
                {wellbeingResources.map(resource => <li key={resource.id} className="flex gap-4 px-5 py-4" data-id="element-11807" data-mp="8ulgpjg5jhin">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-line bg-canvas text-ink-muted" data-id="element-11808" data-mp="dsn9wx7akx7u">
                      <HeadphonesIcon className="h-4 w-4" data-id="element-11809" data-mp="4v7szfcggzs" />
                    </span>
                    <div className="min-w-0" data-id="element-11810" data-mp="3rbfxaepr3gup">
                      <p className="text-[13px] font-semibold text-ink" data-id="element-11811" data-mp="3ramxjxp7lrm2">{resource.title}</p>
                      <p className="cz-num mt-0.5 text-[12px] text-ink-subtle" data-id="element-11812" data-mp="9y2audmm3kwp">
                        {resource.kind} · {resource.minutes} min
                      </p>
                      <p className="mt-1 text-[13px] leading-5 text-ink-muted" data-id="element-11813" data-mp="3ddhk3cn61qgs">
                        {resource.summary}
                      </p>
                    </div>
                  </li>)}
              </ul>
            </Panel>

            <Panel title="Counselling" description="University counselling unit. Appointments are confidential." bodyClassName="p-0" data-id="element-11814" data-mp="3ibswnv4uif2p">
              <ul className="divide-y divide-line" data-id="element-11815" data-mp="xlk8uqwgzf84">
                {counsellingSlots.map(slot => <li key={slot.id} className="px-5 py-4" data-id="element-11816" data-mp="1552yhg07y6po">
                    <p className="text-[13px] font-semibold text-ink" data-id="element-11817" data-mp="153i5hutjyvtx">{slot.counsellor}</p>
                    <p className="mt-0.5 text-[12px] text-ink-muted" data-id="element-11818" data-mp="1a4z5zp84kg9z">{slot.specialism}</p>
                    <p className="cz-num mt-1 text-[12px] text-ink-subtle" data-id="element-11819" data-mp="w5pyrf1wcwi5">
                      {slot.mode} · next {slot.nextAvailable}
                    </p>
                    <Button size="sm" variant="secondary" className="mt-2.5" data-id="element-11820" data-mp="1nvufn3qg71em">
                      Request appointment
                    </Button>
                  </li>)}
              </ul>
              <div className="border-t border-line px-5 py-3" data-id="element-11821" data-mp="1f3shxd7rjz81">
                <p className="text-[12px] leading-5 text-ink-muted" data-id="element-11822" data-mp="1f169zcgynhpa">
                  Requests go directly to the counselling unit. Campuzon staff cannot see the
                  content of your appointment.
                </p>
              </div>
            </Panel>
          </div>
        </div>}

      <EmergencyDialog open={emergencyOpen} onClose={() => setEmergencyOpen(false)} campusId={account.campusId} raisedBy={{
      userId: account.id,
      name: account.fullName,
      handle: account.username
    }} trustedContacts={trustedContacts} onRaised={() => setTab('Safety')} data-id="element-11823" data-mp="2bmzupgokdpnv" />

      <Modal open={incidentOpen} onClose={() => setIncidentOpen(false)} title="Report an incident" description="Goes to campus security for your campus. False reports are treated seriously." footer={<>
            <Button variant="secondary" onClick={() => setIncidentOpen(false)} data-id="element-11825" data-mp="1tppv6uub5r0t">
              Cancel
            </Button>
            <Button variant="primary" disabled={incidentDetail.trim().length < 12} onClick={() => {
        fileIncident({
          category,
          campusId: account.campusId,
          location: anonymous && !incidentPlace ? 'Withheld by reporter' : incidentPlace,
          detail: incidentDetail,
          reported: new Date().toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          }),
          anonymous
        });
        setIncidentPlace('');
        setIncidentDetail('');
        setIncidentOpen(false);
      }} data-id="element-11826" data-mp="1oro1lfz9a9b3">
              Submit report
            </Button>
          </>} data-id="element-11824" data-mp="27gkubff08tr7">
        <div className="space-y-4" data-id="element-11827" data-mp="qv0uu2ytldf9">
          <ChoiceGroup label="Category" options={CATEGORIES} value={category} onChange={setCategory} size="sm" data-id="element-11828" data-mp="vvx0om2qf9nb" />
          <Field label="Where did it happen?" htmlFor="incident-location" data-id="element-11829" data-mp="38121btlr7nw">
            <Input id="incident-location" value={incidentPlace} onChange={event => setIncidentPlace(event.target.value)} placeholder="Building, gate, road or landmark" data-id="element-11830" data-mp="35yfj1rnrwnx" />
          </Field>
          <div data-id="element-11831" data-mp="1icsbclll8p2k">
            <label htmlFor="incident-detail" className="mb-1.5 block text-[13px] font-medium text-ink" data-id="element-11832" data-mp="1idblvh8wsb6k">
              What happened?
            </label>
            <Textarea id="incident-detail" rows={4} value={incidentDetail} onChange={event => setIncidentDetail(event.target.value)} data-id="element-11833" data-mp="1rauaevlrh7n7" />
          </div>
          <div className="border-t border-line pt-2" data-id="element-11834" data-mp="218iisocaidnu">
            <Toggle checked={anonymous} onChange={setAnonymous} label="Report anonymously" description="Security will not see your name. They also will not be able to follow up with you directly." data-id="element-11835" data-mp="2f29jr8bygp7y" />
          </div>
        </div>
      </Modal>

      <Modal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} title="New feedback" description="Choose carefully who should be able to see this." footer={<>
            <Button variant="secondary" onClick={() => setFeedbackOpen(false)} data-id="element-11837" data-mp="37nomz8nj80bo">
              Cancel
            </Button>
            <Button variant="primary" onClick={() => setFeedbackOpen(false)} data-id="element-11838" data-mp="2tr2qa0zekhiu">
              Send feedback
            </Button>
          </>} data-id="element-11836" data-mp="2k25oj9sh8kok">
        <div className="space-y-4" data-id="element-11839" data-mp="2jy3qro2db6kg">
          <Field label="Who is this about?" htmlFor="feedback-target" data-id="element-11840" data-mp="2jwsra0fygkz5">
            <Select id="feedback-target" data-id="element-11841" data-mp="2or6o87a9n05x">
              <option data-id="element-11842" data-mp="2ay7bgd1vbk22">A course or lecturer</option>
              <option data-id="element-11843" data-mp="2azifmdz86ttp">A campus facility</option>
              <option data-id="element-11844" data-mp="2fxbj2z9599qa">Accommodation</option>
              <option data-id="element-11845" data-mp="2fy3d45n3ezeg">A campus service</option>
            </Select>
          </Field>
          <Field label="Subject" htmlFor="feedback-subject" data-id="element-11846" data-mp="20zrh817jnuoc">
            <Input id="feedback-subject" placeholder="One line summary" data-id="element-11847" data-mp="25z4ezgzyem5p" />
          </Field>
          <div data-id="element-11848" data-mp="260yps2higxw2">
            <label htmlFor="feedback-detail" className="mb-1.5 block text-[13px] font-medium text-ink" data-id="element-11849" data-mp="25408irl7h7in">
              Details
            </label>
            <Textarea id="feedback-detail" rows={4} data-id="element-11850" data-mp="2a0gyzsfvnxyw" />
          </div>
          <ChoiceGroup label="Visibility" options={FEEDBACK_VISIBILITY} value={visibility} onChange={setVisibility} size="sm" data-id="element-11851" data-mp="1v740emz7ynem" />
          <p className="flex items-start gap-2 text-[12px] leading-5 text-ink-muted" data-id="element-11852" data-mp="2nuqbsheouwc1">
            <MessageSquareIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-subtle" data-id="element-11853" data-mp="2srzznsmfizxr" />
            {visibility === 'Public' ? 'Other students at your institution will see this and your first name.' : visibility === 'Private' ? 'Only the responsible office sees this, along with your name.' : 'Only a small number of authorized staff can open a confidential report.'}
          </p>
        </div>
      </Modal>
    </div>;
}